import { DeferredMedia } from '@theme/deferred-media'
import { MediaStartedPlayingEvent } from '@theme/events'

const LocalDevHosts = new Set(['localhost', '127.0.0.1', '0.0.0.0'])

const IframeMediaCommands = {
  pause: {
    vimeo: '{"method":"pause"}',
    youtube: '{"event":"command","func":"pauseVideo","args":""}'
  },
  play: {
    vimeo: '{"method":"play"}',
    youtube: '{"event":"command","func":"playVideo","args":""}'
  }
}

class VideoMedia extends DeferredMedia {
  #attachedVideoListeners = new WeakSet()
  #customControlsAbortController = null

  /** @param {MouseEvent} event */
  #onCustomControlClick = (event) => {
    const control =
      event.target instanceof Element
        ? event.target.closest('[data-action]')
        : null
    if (!control || control.closest('ui-video') !== this) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (control.dataset.action === 'play-pause') {
      this._togglePlayPause()
      return
    }

    if (control.dataset.action === 'toggle-mute') {
      this._toggleMute()
    }
  }

  constructor() {
    super()
    this._manualControl = false
    this._setupHoverPlay()
  }

  /** @returns {boolean} */
  get hoverPlay() {
    return this.hasAttribute('hover-play')
  }

  connectedCallback() {
    if (this.hoverPlay && !this.autoplay) {
      this.paused = true
    }

    super.connectedCallback()
    this._setupCustomControls()

    if (this.hoverPlay && !this.autoplay) {
      this._waitForContentAndInit()
    }

    this._updatePlayPauseIcons()

    const video = this.querySelector('video')
    if (video) {
      this._updateMuteIcons(video.muted)
    }
  }

  disconnectedCallback() {
    this.#customControlsAbortController?.abort()
    super.disconnectedCallback()
  }

  /** @returns {boolean} */
  get isLocalDevHost() {
    return LocalDevHosts.has(globalThis.location?.hostname)
  }

  /** @returns {string | null} */
  get localStoreOrigin() {
    if (!this.isLocalDevHost) {
      return null
    }

    if (globalThis.Shopify?.shop) {
      return `https://${globalThis.Shopify.shop}`
    }

    return this.#storeOriginFromFeatures()
  }

  /** @returns {string | null} */
  #storeOriginFromFeatures() {
    const featuresElement = document.getElementById('shopify-features')
    if (!featuresElement?.textContent) {
      return null
    }

    try {
      const features = JSON.parse(featuresElement.textContent)
      return features?.domain ? `https://${features.domain}` : null
    } catch {
      return null
    }
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  normalizeLocalVideoUrl(url) {
    if (!url) {
      return url
    }

    const storeOrigin = this.localStoreOrigin
    if (!storeOrigin) {
      return url
    }

    if (url.startsWith('/cdn/')) {
      return `${storeOrigin}${url}`
    }

    if (url.startsWith('//cdn/')) {
      return `${storeOrigin}/cdn/${url.slice(6)}`
    }

    return url
  }

  /** @param {Element | null} videoNode */
  normalizeLocalVideoNode(videoNode) {
    if (!(videoNode instanceof HTMLElement)) {
      return
    }

    if (videoNode.tagName !== 'VIDEO') {
      return
    }

    this.#normalizeUrlAttribute(videoNode, 'src')
    this.#normalizeUrlAttribute(videoNode, 'poster')

    for (const source of videoNode.querySelectorAll('source')) {
      this.#normalizeUrlAttribute(source, 'src')
    }
  }

  /**
   * @param {Element} node
   * @param {string} attribute
   */
  #normalizeUrlAttribute(node, attribute) {
    const value = node.getAttribute(attribute)
    if (!value) {
      return
    }

    node.setAttribute(attribute, this.normalizeLocalVideoUrl(value))
  }

  /** @returns {HTMLElement | Record<string, () => void> | null} */
  playerTarget() {
    const existingPlayer = this.querySelector('video, iframe[data-video-type]')
    if (existingPlayer) {
      this._attachVideoListeners(existingPlayer)
      this.markMediaLoaded()
      return existingPlayer
    }

    const templateElement = this.querySelector('template')
    if (!templateElement) {
      return this.#noopPlayer()
    }

    this.#appendTemplatedPlayer(templateElement)
    this.markMediaLoaded()

    const player = this.querySelector('video, iframe[data-video-type]')
    if (player) {
      this._attachVideoListeners(player)
    }
    return player
  }

  /** @param {HTMLTemplateElement} templateElement */
  #appendTemplatedPlayer(templateElement) {
    const playerNode =
      templateElement.content.firstElementChild?.cloneNode(true)
    if (!playerNode) {
      return
    }

    this.normalizeLocalVideoNode(playerNode)
    this.appendChild(playerNode)
  }

  /** @returns {Record<string, () => void>} */
  #noopPlayer() {
    const ignoreCommand = () => undefined
    return { mute: ignoreCommand, pause: ignoreCommand, play: ignoreCommand }
  }

  /**
   * @param {HTMLElement | Record<string, () => void>} target
   * @param {string} prop
   */
  playerHandler(target, prop) {
    if (target instanceof HTMLIFrameElement) {
      this.postIframeMediaCommand(target, prop)
      return
    }

    if (prop === 'play') {
      this.#playNativeVideo(target)
      return
    }

    if (typeof target?.[prop] === 'function') {
      target[prop]()
    }
  }

  /** @param {HTMLVideoElement} target */
  #playNativeVideo(target) {
    const playPromise = target.play?.()
    if (!(playPromise instanceof Promise)) {
      return
    }

    playPromise.catch((error) => {
      if (error.name === 'NotAllowedError') {
        this.#suspendPlayback(target)
      }
    })
  }

  /** @param {HTMLVideoElement} target */
  #suspendPlayback(target) {
    this.setAttribute('suspended', '')
    target.controls = true

    const replacementImageSrc = target.previousElementSibling?.currentSrc
    if (replacementImageSrc) {
      target.poster = replacementImageSrc
    }
  }

  /**
   * @param {HTMLIFrameElement} target
   * @param {string} prop
   */
  postIframeMediaCommand(target, prop) {
    const command = IframeMediaCommands[prop]
    if (!command) {
      return
    }

    if (prop === 'play') {
      this.dispatchEvent(new MediaStartedPlayingEvent(this))
      this.setAttribute('playing', '')
    } else {
      this.removeAttribute('playing')
    }

    this.updatePlayPauseHint(prop === 'play')
    target.contentWindow?.postMessage(
      target.dataset.videoType === 'youtube' ? command.youtube : command.vimeo,
      '*'
    )
  }

  /** @param {HTMLVideoElement} player */
  #onVideoPlay(player) {
    const suppressUnexpectedAutoplay =
      this.hoverPlay && !this._videoStarted && this.paused

    if (suppressUnexpectedAutoplay) {
      this._videoStarted = true
      player.pause()
      return
    }

    this._videoStarted = true
    this.dispatchEvent(new MediaStartedPlayingEvent(this))
    this.setAttribute('playing', '')
    this.removeAttribute('suspended')
    this._updatePlayPauseIcons()
  }

  /** @param {HTMLVideoElement} player */
  #onVideoPause(player) {
    if (player.seeking || !player.paused) {
      return
    }

    this.removeAttribute('playing')
    this._updatePlayPauseIcons()
  }

  _setupHoverPlay() {
    if (!this.hoverPlay) {
      return
    }

    this.addEventListener('mouseenter', () => {
      if (this._manualControl) {
        return
      }
      this.paused = false
      this.play()
    })

    this.addEventListener('mouseleave', () => {
      if (this._manualControl) {
        return
      }
      this.paused = true
      this.pause()
    })
  }

  _setupCustomControls() {
    this.#customControlsAbortController?.abort()
    this.#customControlsAbortController = new AbortController()
    this.addEventListener('click', this.#onCustomControlClick, {
      signal: this.#customControlsAbortController.signal
    })
  }

  _togglePlayPause() {
    this._manualControl = true

    if (this.playing) {
      this.paused = true
      this.pause()
      this._manualControl = false
    } else {
      this.paused = false
      this.play()
    }

    this._updatePlayPauseIcons()
  }

  _toggleMute() {
    const video = this.querySelector('video')
    if (!video) {
      return
    }

    video.muted = !video.muted
    this._updateMuteIcons(video.muted)
  }

  _updatePlayPauseIcons() {
    this.updatePlayPauseHint(this.playing)
  }

  /** @param {boolean} isMuted */
  _updateMuteIcons(isMuted) {
    const volumeOnIcon = this.querySelector('.video-icon-volume-on')
    const volumeOffIcon = this.querySelector('.video-icon-volume-off')
    if (!(volumeOnIcon && volumeOffIcon)) {
      return
    }

    volumeOnIcon.classList.toggle('hidden', isMuted)
    volumeOffIcon.classList.toggle('hidden', !isMuted)
  }

  /**
   * @param {Element} player
   * @returns {Element}
   */
  _attachVideoListeners(player) {
    if (!(player instanceof HTMLVideoElement)) {
      return player
    }

    if (this.#attachedVideoListeners.has(player)) {
      return player
    }
    this.#attachedVideoListeners.add(player)

    if (this.autoplay && player.loop) {
      player.muted = true
    }

    player.addEventListener('play', () => this.#onVideoPlay(player))
    player.addEventListener('pause', () => this.#onVideoPause(player))
    player.addEventListener('volumechange', () => {
      this._updateMuteIcons(player.muted)
    })
    return player
  }
}

if (!customElements.get('ui-video')) {
  customElements.define('ui-video', VideoMedia)
}
